import React, { useState, useEffect } from "react";
import { Modal } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Form, Formik } from "formik";
import UserDetailsForm from "pages/authentication/workspace/Steps/UserDetailsForm";
import { WORKSPACE_DROPDOWN_LISTS } from "lib/constants";
import { getAPICall } from "lib/callApi";
import * as Yup from "yup";
import { enqueueSnackbar } from "notistack";
import useAuth from "auth/useAuth";

const createModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: window.innerWidth < 400 ? "100%" : 500,
  maxHeight: "80%",
  overflowX: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  "& .MuiFormHelperText-root": { color: "#d32f2f" },
  borderRadius: "0.5rem",
};

function UpdateProfile({ open, handleClose, user }) {
  const { updateUserProfile, error } = useAuth();
  const [timeAtCompanyOptions, setTimeAtCompanyOptions] = useState([]);
  const [averageSalesCycleLengthOptions, setAverageSalesCycleLengthOptions] = useState([]);
  const [image, setImage] = useState();
  const [profilePicture, setProfilePicture] = useState("");

  const fieldsToInclude = {
    name: false,
    role: false,
    timeAtCompany: true,
    averageSalesCycleLength: true,
    profilePicture: !user?.profilePicture,
  };

  const validationSchema = Yup.object({
    timeAtCompany: Yup.object({
      value: Yup.string().required("Time at company is required"),
    }).typeError("Time at company is required"),
    averageSalesCycleLength: Yup.object({
      value: Yup.string().required("Average sales cycle length is required"),
    }).typeError("Average sales cycle length is required"),
  });

  const [initialValues, setInitialValues] = useState({
    timeAtCompany: null,
    averageSalesCycleLength: null,
  });

  const fetchWorkspaceDropdownsLists = async () => {
    try {
      const allDropDownLists = await getAPICall(WORKSPACE_DROPDOWN_LISTS, true);
      if (allDropDownLists?.data?.success) {
        setTimeAtCompanyOptions(allDropDownLists.data?.data?.timeAtCompany || []);
        setAverageSalesCycleLengthOptions(
          allDropDownLists.data?.data?.averageSalesCycleLength || []
        );
      } else if (allDropDownLists.response.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: allDropDownLists.response.data.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: err.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    fetchWorkspaceDropdownsLists();
  }, []);

  useEffect(() => {
    if (user) {
      const initialTimeAtCompany =
        user?.UserCompaniesLinks?.[0]?.FK_user_company_tenure_years_id ||
        user?.UserCompaniesLinks?.FK_user_company_tenure_years_id;
      const initialAverageSalesCycleLength =
        user?.UserCompaniesLinks?.[0]?.FK_user_sales_cycles_id ||
        user?.UserCompaniesLinks?.FK_user_sales_cycles_id;

      setInitialValues({
        timeAtCompany: initialTimeAtCompany
          ? timeAtCompanyOptions?.find((option) => option?.value === initialTimeAtCompany)
          : null,
        averageSalesCycleLength: initialAverageSalesCycleLength
          ? averageSalesCycleLengthOptions?.find(
              (option) => option?.value === initialAverageSalesCycleLength
            )
          : null,
      });
    }
  }, [user, timeAtCompanyOptions, averageSalesCycleLengthOptions]);

  const handleSubmit = async (data) => {
    const dataToPass = {
      timeAtCompany: data.timeAtCompany.value,
      averageSalesCycleLength: data.averageSalesCycleLength.value,
      ...(profilePicture && image
        ? {
            profilePicture: {
              image: profilePicture,
              name: `${user.email.split(" ").join("-")}.${image.type.split("/")[1]}`.toLowerCase(),
              type: image.type,
            },
          }
        : {}),
    };
    const updateProfileResponse = await updateUserProfile(dataToPass);
    if (updateProfileResponse) handleClose();
  };

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={createModalStyle}>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="secondary"
          mx={2}
          my={1}
          p={3}
          textAlign="center"
        >
          <MDBox sx={{ display: "flex", justifyContent: "center", padding: ".5rem" }}>
            <MDTypography id="modal-modal-title" variant="h5" component="h2" color="white">
              Update Profile
            </MDTypography>
          </MDBox>
        </MDBox>

        <MDBox sx={{ display: "flex", justifyContent: "center", padding: "1rem" }}>
          <MDTypography
            id="update-profile-modal-body"
            component="p"
            color="black"
            textAlign="center"
            fontSize="medium"
          >
            Alysio is always working to improve the user experience. Please complete the two
            questions below to help us with future product enhancements. Thank you, Alysio Team
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <Formik
            enableReinitialize
            validateOnChange
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, touched, errors }) => (
              <Form>
                <UserDetailsForm
                  formProps={{ values, setFieldValue, touched, errors }}
                  setImage={setImage}
                  selectedTeam={null}
                  profilePicture={profilePicture}
                  setProfilePicture={setProfilePicture}
                  timeAtCompanyOptions={timeAtCompanyOptions}
                  averageSalesCycleLengthOptions={averageSalesCycleLengthOptions}
                  fieldsToInclude={fieldsToInclude}
                />

                {error ? <span style={{ color: "#d32f2f", fontSize: "12px" }}>{error}</span> : null}

                <MDBox mt={4} mb={1}>
                  <MDButton color="primary" fullWidth type="submit">
                    Update
                  </MDButton>
                </MDBox>
              </Form>
            )}
          </Formik>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

export default UpdateProfile;
